/**
 * Grouping for the Tool (SMSWiser) tables: per-dimension group-by options and a
 * tool-metric group aggregator (StatsTable renders the groups; aggregateVoluum
 * only sums tracker keys, so tool blocks need their own subtotal math).
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  GenericStatsRow,
  StatsGroupGeneric,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { ToolStatsDimension } from '@/hooks/smswiser/tool-stats';

export interface ToolGroupOption {
  value: string;
  label: string;
  keyOf: (row: GenericStatsRow) => string;
}

type Sort = { field: string; dir: 'asc' | 'desc' } | null;

const NONE: ToolGroupOption = { value: 'none', label: 'No grouping', keyOf: () => '' };

function tld(domain: string): string {
  const parts = domain.split('.');
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : domain || '—';
}

/** Every sensible grouping per tool dimension (label = sender/batch, subLabel = route). */
export const TOOL_GROUP_OPTIONS: Record<ToolStatsDimension, ToolGroupOption[]> = {
  abTesting: [
    NONE,
    { value: 'sender', label: 'Sender', keyOf: (r) => r.label || '—' },
    { value: 'route', label: 'Route', keyOf: (r) => r.subLabel || '—' },
  ],
  batch: [
    NONE,
    { value: 'routeSender', label: 'Route · Sender', keyOf: (r) => r.subLabel || '—' },
  ],
  shortlink: [NONE, { value: 'tld', label: 'TLD', keyOf: (r) => tld(r.label) }],
  sms: [NONE],
};

/** Group rows by their source drop — only meaningful in the aggregated multi view. */
export const TOOL_DROP_GROUP_OPTION: ToolGroupOption = {
  value: 'drop',
  label: 'Drop',
  keyOf: (r) => r.dropId || '—',
};

/** Multi-drop options: No grouping, By drop (default), then the dimension's own keys. */
export function toolMultiGroupOptions(dimension: ToolStatsDimension): ToolGroupOption[] {
  const dimSpecific = TOOL_GROUP_OPTIONS[dimension].filter((o) => o.value !== 'none');
  return [NONE, TOOL_DROP_GROUP_OPTION, ...dimSpecific];
}

const SUM_KEYS = [
  'sent',
  'clicks',
  'bot',
  'revenue',
  'conversions',
  'costs',
  'profit',
  'delivered',
  'undeliverable',
] as const;

/** Sum tool metrics across rows and recompute the ratio metrics from the sums. */
export function aggregateToolMetrics(rows: GenericStatsRow[]): Record<string, number> {
  const acc: Record<string, number> = {};
  const seen = new Set<string>();
  for (const r of rows) {
    const m = r.voluum ?? {};
    for (const k of SUM_KEYS) {
      if (typeof m[k] === 'number') {
        acc[k] = (acc[k] ?? 0) + m[k]!;
        seen.add(k);
      }
    }
  }
  acc.ctr = acc.sent > 0 ? ((acc.clicks ?? 0) / acc.sent) * 100 : 0;
  acc.epc = acc.clicks > 0 ? (acc.revenue ?? 0) / acc.clicks : 0;
  acc.roi = acc.costs > 0 ? ((acc.profit ?? 0) / acc.costs) * 100 : 0;
  for (const k of ['delivered', 'undeliverable'] as const) {
    if (!seen.has(k)) delete acc[k];
  }
  return acc;
}

function sortToolGroups(groups: StatsGroupGeneric[], sort: Sort): StatsGroupGeneric[] {
  if (!sort) return groups;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...groups].sort((a, b) => {
    if (sort.field === 'label') return a.label.localeCompare(b.label) * factor;
    return ((a.totals[sort.field] ?? 0) - (b.totals[sort.field] ?? 0)) * factor;
  });
}

export function buildToolGroups(
  rows: GenericStatsRow[],
  keyOf: (row: GenericStatsRow) => string,
  sort: Sort,
): StatsGroupGeneric[] {
  const byKey = new Map<string, GenericStatsRow[]>();
  for (const r of rows) {
    const key = keyOf(r) || '—';
    const list = byKey.get(key) ?? [];
    list.push(r);
    byKey.set(key, list);
  }
  const groups = Array.from(byKey.entries()).map(([key, groupRows]) => ({
    key,
    label: key,
    rows: groupRows,
    totals: aggregateToolMetrics(groupRows),
  }));
  return sortToolGroups(groups, sort);
}
